/**
 * Adaptateur synchronisé : le local d'abord, le serveur ensuite.
 *
 * Contrainte de classe : le réseau d'un collège tombe. Une partie ne doit
 * jamais être perdue parce que le Wi-Fi a clignoté. L'écriture locale est donc
 * immédiate et systématique ; l'envoi au serveur est mis en file d'attente, et
 * réessayé tout seul jusqu'à ce qu'il passe — sans attendre que l'élève touche
 * de nouveau la tablette.
 *
 * Le verrou optimiste du serveur est respecté : si une autre tablette a écrit
 * entre-temps, l'état passe en « conflit » et l'envoi s'arrête. On avertit
 * plutôt que d'écraser.
 */
import { deserialize, serialize } from '@neopolis/engine';
import type { GameState } from '@neopolis/engine';

import type { ClientApi, EtatDeLIlot } from './client-api.js';
import type { SaveAdapter } from './save-adapter.js';

export type EtatDeSynchronisation = 'synchronise' | 'en_attente' | 'hors_ligne' | 'conflit';

export interface AdaptateurSynchronise extends SaveAdapter {
  /**
   * Chapitres ouverts par l'enseignant, tels que le serveur les a annoncés.
   * `null` tant qu'on ne le sait pas — auquel cas rien n'est verrouillé : une
   * classe sans serveur ne doit pas se retrouver bloquée.
   */
  chapitresOuverts(): readonly string[] | null;
  /** S'abonne à l'état de synchronisation. Renvoie de quoi se désabonner. */
  surChangement(ecouteur: (etat: EtatDeSynchronisation) => void): () => void;
  /** Force l'envoi de ce qui attend. Appelé à chaque fin d'étape et au retour du réseau. */
  synchroniser(): Promise<void>;
  /**
   * Le serveur connaît-il ce code d'îlot ?
   *
   * Sert à refuser un code mal recopié **avant** de lancer une partie, plutôt
   * que de laisser l'îlot jouer une heure dans le vide.
   */
  verifierIlot(islandCode: string): Promise<EtatDeLIlot>;
  etat(): EtatDeSynchronisation;
}

export interface OptionsSynchronisation {
  /** Délai de regroupement des écritures, pour ne pas appeler l'API à chaque clic. */
  delaiMs?: number;
  /** Planificateur, remplaçable dans les tests. */
  programmer?: (action: () => void, delai: number) => () => void;
  /** Intervalle entre deux tentatives quand le réseau est tombé. */
  delaiRepriseMs?: number;
  /**
   * Planificateur des reprises, séparé du précédent.
   *
   * Les tests remplacent souvent `programmer` par un exécuteur immédiat ; s'il
   * servait aussi aux reprises, une panne de réseau ferait boucler la reprise
   * sur elle-même sans jamais rendre la main.
   */
  programmerReprise?: (action: () => void, delai: number) => () => void;
}

const programmerParDefaut = (action: () => void, delai: number): (() => void) => {
  const minuteur = setTimeout(action, delai);
  return () => clearTimeout(minuteur);
};

export function createSyncingAdapter(
  local: SaveAdapter,
  client: ClientApi,
  options: OptionsSynchronisation = {},
): AdaptateurSynchronise {
  const delaiMs = options.delaiMs ?? 1000;
  const programmer = options.programmer ?? programmerParDefaut;
  const delaiRepriseMs = options.delaiRepriseMs ?? 5000;
  const programmerReprise = options.programmerReprise ?? programmerParDefaut;

  /** Dernière révision connue du serveur, par îlot. */
  const revisions = new Map<string, number>();
  /** Le dernier état à envoyer, par îlot. Une écriture plus récente remplace l'ancienne. */
  const enAttente = new Map<string, GameState>();
  const ecouteurs = new Set<(etat: EtatDeSynchronisation) => void>();

  let etatCourant: EtatDeSynchronisation = 'synchronise';
  let chapitres: readonly string[] | null = null;
  let annulerMinuteur: (() => void) | null = null;
  let annulerReprise: (() => void) | null = null;
  let envoiEnCours: Promise<void> | null = null;

  const changerEtat = (suivant: EtatDeSynchronisation): void => {
    if (etatCourant === suivant) {
      return;
    }
    etatCourant = suivant;
    for (const ecouteur of ecouteurs) {
      ecouteur(suivant);
    }
  };

  async function envoyerTout(): Promise<void> {
    // Le conflit est verrouillant : une fois constaté, plus rien ne part tant
    // que l'élève n'a pas rechargé. La garde est ici, et pas seulement à
    // l'entrée de `synchroniser`, parce qu'un envoi déjà mis en file peut
    // s'exécuter après celui qui a découvert le conflit — et remettrait alors
    // l'indicateur au vert.
    if (etatCourant === 'conflit') {
      return;
    }

    for (const [islandCode, state] of [...enAttente]) {
      const revision = revisions.get(islandCode) ?? 0;
      const resultat = await client.enregistrer(islandCode, revision, serialize(state));

      if (resultat.ok) {
        revisions.set(islandCode, resultat.revision);
        // Ne retirer que si rien de plus récent n'est arrivé entre-temps.
        if (enAttente.get(islandCode) === state) {
          enAttente.delete(islandCode);
        }
        continue;
      }

      if (resultat.raison === 'conflit') {
        // Une autre tablette a pris la main. On garde la révision du serveur
        // pour que la reprise parte d'une base saine, et on arrête d'écrire.
        revisions.set(islandCode, resultat.revision);
        enAttente.delete(islandCode);
        changerEtat('conflit');
        return;
      }

      // Réseau ou serveur indisponible : on garde en attente, et on réessaiera
      // tout seul. Sans cette reprise, « on réessaiera » n'était qu'une
      // intention : rien ne rappelait `synchroniser`, donc un îlot qui perdait
      // le réseau puis posait sa tablette ne renvoyait jamais son travail.
      changerEtat('hors_ligne');
      planifierReprise();
      return;
    }

    changerEtat(enAttente.size === 0 ? 'synchronise' : 'en_attente');
  }

  /**
   * Programme une nouvelle tentative tant qu'il reste quelque chose à envoyer.
   *
   * On ne s'appuie pas sur l'événement « online » du navigateur : il ne dit que
   * si une interface réseau est active, pas si le serveur répond — un portail
   * captif de collège coche la première case sans la seconde.
   */
  function planifierReprise(): void {
    if (annulerReprise !== null) {
      return;
    }
    annulerReprise = programmerReprise(() => {
      annulerReprise = null;
      if (enAttente.size > 0 && etatCourant !== 'conflit') {
        void synchroniser();
      }
    }, delaiRepriseMs);
  }

  const synchroniser = (): Promise<void> => {
    if (etatCourant === 'conflit') {
      return Promise.resolve();
    }
    envoiEnCours = (envoiEnCours ?? Promise.resolve()).then(envoyerTout);
    return envoiEnCours;
  };

  const programmerEnvoi = (): void => {
    annulerMinuteur?.();
    annulerMinuteur = programmer(() => {
      annulerMinuteur = null;
      void synchroniser();
    }, delaiMs);
  };

  return {
    async charger(islandCode) {
      const localState = await local.charger(islandCode);

      const distant = await client.charger(islandCode);
      if (distant) {
        revisions.set(islandCode, distant.revision);
        chapitres = distant.chapitres ?? null;
      }

      /*
       * Qui fait foi ?
       *
       * Le serveur porte le travail des autres tablettes de l'îlot, donc il
       * gagne en général. Mais l'envoi est différé d'une seconde alors que
       * l'écriture locale est immédiate : entre les deux, la tablette est en
       * avance sur le serveur. Faire gagner le serveur sans regarder revenait
       * à effacer tout ce qui avait été fait depuis le dernier envoi —
       * rechargement de page, tablette qui se verrouille, onglet rouvert.
       *
       * `history` tranche : le moteur y ajoute une action à chaque geste, donc
       * sa longueur ne fait que croître pour un même îlot. Le serveur ne gagne
       * que s'il n'est pas en retard.
       */
      const relu = distant?.etat ? deserialize(distant.etat) : null;
      const distantState = relu?.ok ? relu.state : null;

      if (
        distantState &&
        (!localState || distantState.history.length >= localState.history.length)
      ) {
        await local.enregistrer(distantState);
        return distantState;
      }

      if (distantState && localState) {
        // La tablette est en avance : on garde son état, et on le met en file
        // pour que le serveur rattrape son retard.
        enAttente.set(islandCode, localState);
        changerEtat('en_attente');
        programmerEnvoi();
      }

      return localState;
    },

    async enregistrer(state) {
      await local.enregistrer(state);
      enAttente.set(state.islandCode, state);
      if (etatCourant !== 'conflit') {
        changerEtat('en_attente');
      }
      programmerEnvoi();
    },

    async effacer(islandCode) {
      enAttente.delete(islandCode);
      revisions.delete(islandCode);
      await local.effacer(islandCode);
    },

    dernierIlot: () => local.dernierIlot(),

    surChangement(ecouteur) {
      ecouteurs.add(ecouteur);
      return () => ecouteurs.delete(ecouteur);
    },

    synchroniser,
    verifierIlot: (islandCode) => client.verifierIlot(islandCode),
    etat: () => etatCourant,
    chapitresOuverts: () => chapitres,
  };
}
